import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

// Session storage key for storing redirect paths
const REDIRECT_PATH_KEY = 'auth_redirect_path';

/**
 * Hook for handling authentication redirects
 * @param redirectIfAuthenticated - Whether to redirect if user is authenticated
 * @param redirectPath - Path to redirect to (defaults based on user role)
 */
export const useAuthRedirect = (
  redirectIfAuthenticated = true,
  redirectPath?: string
) => {
  const { isAuthenticated, isAdmin, isLoading, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [redirectAttempts, setRedirectAttempts] = useState(0);

  // Define public routes that don't require authentication
  const publicRoutes = ['/login', '/', '/invitation', '/signup'];
  const isPublicRoute = publicRoutes.includes(location.pathname) || 
                       location.pathname.includes('/invitation/');
  
  // Don't redirect more than 3 times to prevent infinite redirects
  const maxRedirectAttempts = 3;
  
  // FIX: Add debug info for easier troubleshooting
  useEffect(() => {
    if (redirectAttempts > 0) {
      //console.log(`Redirect attempt ${redirectAttempts}/${maxRedirectAttempts} at path: ${location.pathname}`);
    }
  }, [redirectAttempts, location.pathname, maxRedirectAttempts]);

  // Save the current path to restore it later
  const saveRedirectPath = useCallback((path: string) => {
    // Don't save login page or other public routes
    if (!publicRoutes.includes(path) && !path.includes('/invitation/')) {
      sessionStorage.setItem(REDIRECT_PATH_KEY, path);
    }
  }, [publicRoutes]);

  // Get the saved redirect path
  const getSavedRedirectPath = useCallback(() => {
    return sessionStorage.getItem(REDIRECT_PATH_KEY);
  }, []);

  // Clear the saved redirect path
  const clearSavedRedirectPath = useCallback(() => {
    sessionStorage.removeItem(REDIRECT_PATH_KEY);
  }, []);

  // Memoize the redirect logic to prevent unnecessary reruns
  const performRedirect = useCallback(() => {
    // Only attempt redirect if auth loading is complete and we haven't redirected yet
    // and we haven't exceeded max redirect attempts
    if (isLoading) {
      // Don't redirect while still loading auth state
      return;
    }
    
    if (hasRedirected) {
      // Already redirected this session
      return;
    }
    
    if (redirectAttempts >= maxRedirectAttempts) {
      console.error('Maximum redirect attempts reached. Stopping to prevent infinite redirect loop.');
      return;
    }
    
    //console.log('Evaluating redirect conditions:', {
    //  isAuthenticated,
    //  redirectIfAuthenticated,
    //  path: location.pathname,
    //  isPublicRoute,
    //  session: !!session,
    //  redirectAttempts
    //});

    // Increment redirect attempts
    setRedirectAttempts(prev => prev + 1);

    // For admin routes, check if user is admin
    const isAdminRoute = location.pathname.startsWith('/admin');
    if (isAuthenticated && isAdminRoute && !isAdmin) {
      //console.log('User is not an admin, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
      setHasRedirected(true);
      return;
    }

    // Handle redirects based on authentication state
    if (redirectIfAuthenticated && isAuthenticated && location.pathname === '/login') {
      // Check if we have a saved path to redirect to
      const savedPath = getSavedRedirectPath();
      const destination = savedPath || redirectPath || (isAdmin ? '/admin/dashboard' : '/dashboard');
      
      //console.log(`Redirecting authenticated user to: ${destination} from: ${location.pathname}`);
      navigate(destination, { replace: true });
      
      // Clear the saved path after redirecting
      clearSavedRedirectPath();
      setHasRedirected(true);
    } else if (!redirectIfAuthenticated && !isAuthenticated && !isPublicRoute) {
      // If we need authentication but user is not authenticated, save the current path and redirect to login
      //console.log(`Saving current path (${location.pathname}) and redirecting to login`);
      saveRedirectPath(location.pathname);
      navigate('/login', { replace: true });
      setHasRedirected(true);
    }
  },
    [
      isAuthenticated, 
      isAdmin, 
      isLoading, 
      navigate, 
      redirectIfAuthenticated, 
      redirectPath, 
      hasRedirected,
      location.pathname,
      isPublicRoute,
      session,
      redirectAttempts,
      maxRedirectAttempts,
      saveRedirectPath,
      getSavedRedirectPath,
      clearSavedRedirectPath
    ]
  );

  // Effect to perform the redirect
  useEffect(() => {
    // FIX: Only run this if we're not at the max redirects already
    if (redirectAttempts >= maxRedirectAttempts) {
      return;
    }
    
    // Small delay to ensure auth state is stable
    const redirectTimer = setTimeout(() => {
      performRedirect();
    }, 300);  // Increased timeout to ensure auth state is fully settled
    
    return () => clearTimeout(redirectTimer);
  }, [performRedirect, redirectAttempts, maxRedirectAttempts]);

  // Reset redirect flag when location changes
  useEffect(() => {
    // FIX: Only reset if we're on a completely different path, not just params
    const currentMainPath = location.pathname.split('/').slice(0, 3).join('/');
    
    // Store the last path to detect real navigation changes vs. param changes
    const lastPath = sessionStorage.getItem('lastRedirectPath');
    
    if (lastPath !== currentMainPath) {
      //console.log(`Path changed from ${lastPath} to ${currentMainPath}, resetting redirect state`);
      setHasRedirected(false);
      setRedirectAttempts(0);
      sessionStorage.setItem('lastRedirectPath', currentMainPath);
    }
  }, [location.pathname]);

  return { isAuthenticated, isAdmin, isLoading, session };
};
